// src/render/videoBlurCanvas.ts
import { log } from "../utils/debug";
import { normalizeColor } from "../utils/color";

const ua = navigator.userAgent;
const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
const isChrome = /Chrome|CriOS/i.test(ua) && !/Edg|OPR|Brave|SamsungBrowser/i.test(ua);

export interface CanvasConfig {
  source: string;
  effectiveLayout: string;
  /** Optional: if omitted, suppression is computed internally */
  suppressBlurAndGradient?: boolean;
  isVerticalVideo: boolean;
  isVerticalScreen: boolean;
}

export function setupVideoCanvas(
  section: HTMLElement,
  video: HTMLVideoElement,
  config: CanvasConfig
): void {
  const { effectiveLayout, isVerticalVideo, isVerticalScreen } = config;

  if (isVerticalScreen) {
    log("[CANVAS] Skipping canvas creation: portrait screen");
    return;
  }

  // Determine background *image* presence (attributes, CSS var, or computed style)
  const cs = getComputedStyle(section);
  const bgImageVar = cs.getPropertyValue("--section-bg-image");
  const bgImageStyle = cs.backgroundImage;
  const hasBgImageAttr =
    section.hasAttribute("background-h") || section.hasAttribute("background-v");
  const hasBgImageVar = !!(bgImageVar && bgImageVar.trim() !== "");
  const hasBgImageCSS = !!(bgImageStyle && bgImageStyle !== "none");
  const hasBackgroundImage = hasBgImageAttr || hasBgImageVar || hasBgImageCSS;

  // Respect override if provided; otherwise suppress when any background image is present
  const suppress =
    config.suppressBlurAndGradient ??
    hasBackgroundImage;

  if (suppress) {
    log("[CANVAS] Skipping canvas creation: suppressed by background or config", {
      effectiveLayout,
      hasBgImageAttr,
      hasBgImageVar,
      hasBgImageCSS,
      bgImageVar,
      bgImageStyle,
      isVerticalVideo,
      isVerticalScreen,
    });
    return;
  }

  // Broaden support: allow reels (left/right/center) and standard video (top/bottom/center)
  const supportedLayouts = ["left", "right", "center", "top", "bottom"];
  const isReelLayout = ["left", "right", "center"].includes(effectiveLayout);
  const isVideoLayout = ["top", "bottom", "center"].includes(effectiveLayout);

  if (!(isReelLayout || isVideoLayout)) {
    log("[CANVAS] Skipping canvas creation: layout not supported", {
      effectiveLayout,
      isVerticalVideo,
      isVerticalScreen,
    });
    return;
  }

  // Drop Safari-specific fallbacks; mirror Chrome behavior for consistent blur.

  log("[CANVAS] Creating canvas for blurred video effect");

  // Hint CSS that blurred gutters are present so text can switch to high-contrast styling.
  try {
    if (isReelLayout) section.setAttribute("data-blur-gutters", "1");
  } catch {}

  const canvas = document.createElement("canvas");
  canvas.className = "sc-canvas bg-image-layer";
  Object.assign(canvas.style, {
    position: "absolute",
    inset: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
  } as Partial<CSSStyleDeclaration>);
  section.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.warn("[CANVAS] Failed to get 2D context");
    return;
  }

  // Mirror Chrome: use devicePixelRatio for a sharper blur pass.
  const scale = window.devicePixelRatio || 2;

  // Compute base color to paint under the blur
  const rawBgColor =
    cs.getPropertyValue("--section-bg").trim() ||
    (section as HTMLElement).style.backgroundColor ||
    cs.backgroundColor ||
    "#000";
  const baseBgColor = normalizeColor(rawBgColor, "#000");

  const resizeCanvas = () => {
    const w = section.clientWidth;
    const h = section.clientHeight;
    canvas.width = w * scale;
    canvas.height = h * scale;
    ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform
    ctx.scale(scale, scale);
  };
  resizeCanvas();
  window.addEventListener("resize", () => {
    resizeCanvas();
    drawBlurredVideo();
  });

  let animationFrameId: number | null = null;
  let timeoutId: number | null = null;

  const drawBlurredVideo = () => {
    if (!video || !video.videoWidth || video.readyState < 2) {
      // Even if not ready, keep a painted background to avoid black gutters.
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = baseBgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      if (timeoutId !== null) clearTimeout(timeoutId);
      // Try again shortly; iOS may delay readyState.
      timeoutId = window.setTimeout(() => {
        animationFrameId = requestAnimationFrame(drawBlurredVideo);
      }, 120);
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Fill behind blur so gutters never flash white
    ctx.fillStyle = baseBgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Slightly stronger blur on Chrome to keep gutters soft.
    const blurAmount = isChrome ? 48 : 40;
    ctx.filter = `blur(${blurAmount}px)`;
    canvas.style.filter = "";

    const aspectRatio = video.videoWidth / video.videoHeight;
    const canvasRatio = canvas.width / canvas.height;
    let drawWidth = canvas.width / scale;
    let drawHeight = canvas.height / scale;
    let dx = 0, dy = 0;

    if (aspectRatio > canvasRatio) {
      // Video wider than canvas
      drawHeight = canvas.height / scale;
      drawWidth = drawHeight * aspectRatio;
      dx = (canvas.width / scale - drawWidth) / 2;
    } else {
      // Video taller than canvas
      drawWidth = canvas.width / scale;
      drawHeight = drawWidth / aspectRatio;

      if (isVideoLayout && effectiveLayout === "top") {
        dy = 0; // align to top
      } else {
        dy = (canvas.height / scale - drawHeight) / 2; // center
      }
    }

    // Align horizontally based on reel layout (left/right/center)
    if (isReelLayout) {
      const cw = canvas.width / scale;
      if (effectiveLayout === "left") {
        dx = 0;
      } else if (effectiveLayout === "right") {
        dx = cw - drawWidth;
      } else {
        dx = (cw - drawWidth) / 2; // center
      }
    }

    ctx.drawImage(video, dx, dy, drawWidth, drawHeight);
    ctx.filter = "none";
    // Darken gutters a bit to mask residual artifacts
    let gutterDarkness = 0.55;

    ctx.fillStyle = `rgba(0, 0, 0, ${gutterDarkness})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    timeoutId = window.setTimeout(() => {
      animationFrameId = requestAnimationFrame(drawBlurredVideo);
    }, 100);
  };

  video.oncanplaythrough = drawBlurredVideo;
  video.onplay = drawBlurredVideo;
  video.onloadeddata = drawBlurredVideo;
  video.onloadedmetadata = () => {
    try { if (video.currentTime === 0) video.currentTime = 0.001; } catch {}
    drawBlurredVideo();
  };
  video.onpause = () => {
    if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
    if (timeoutId !== null) clearTimeout(timeoutId);
    // Draw one last frame so the blurred background matches the paused video
    drawBlurredVideo();
  };
  video.onended = video.onpause;

  if ((video as any).requestVideoFrameCallback) {
      (video as any).requestVideoFrameCallback(() => drawBlurredVideo());
  }

  if (video.readyState >= 2) {
    drawBlurredVideo();
  }
}
